import { MessageCircle, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useWhatsApp, useWorkingHours } from "@/hooks/useWhatsApp";
import { cn } from "@/lib/utils";
import { Button } from "./button";

interface WhatsAppFloatProps {
  className?: string;
}

const WhatsAppFloat = ({ className }: WhatsAppFloatProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { openWhatsApp } = useWhatsApp();
  const { isWorkingHours, getWorkingHoursMessage } = useWorkingHours();

  // Show button after page load with animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2000); // Show after 2 seconds

    return () => clearTimeout(timer);
  }, []);

  // Auto show tooltip after some time
  useEffect(() => {
    if (isVisible) {
      const tooltipTimer = setTimeout(() => {
        setShowTooltip(true);
        // Auto hide tooltip after 5 seconds
        setTimeout(() => setShowTooltip(false), 5000);
      }, 5000); // Show tooltip after 5 seconds

      return () => clearTimeout(tooltipTimer);
    }
  }, [isVisible]);

  const handleWhatsAppClick = () => {
    openWhatsApp('contact');
    setShowTooltip(false);
  };

  const handleTooltipClose = () => {
    setShowTooltip(false);
  };

  if (!isVisible) return null;

  return (
    <div className={cn(
      "fixed bottom-6 right-6 z-50 flex items-end space-x-3",
      "animate-in slide-in-from-bottom-8 duration-700",
      className
    )}>
      {/* Tooltip */}
      {showTooltip && (
        <div className={cn(
          "relative bg-card/95 backdrop-blur-sm p-4 rounded-2xl shadow-elegant max-w-xs",
          "animate-in slide-in-from-right-4 duration-300",
          "border border-border"
        )}>
          <button
            onClick={handleTooltipClose}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="pr-6">
            <p className="text-sm font-medium mb-2 text-foreground">
              Precisa de ajuda?
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              {getWorkingHoursMessage()}
            </p>
            <Button
              size="sm"
              onClick={handleWhatsAppClick}
              className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white"
            >
              <MessageCircle className="w-3 h-3 mr-2" />
              Conversar agora
            </Button>
          </div>

          {/* Tooltip arrow */}
          <div className="absolute top-1/2 -right-2 transform -translate-y-1/2">
            <div className="w-4 h-4 bg-card border-r border-b border-border transform rotate-45"></div>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <button
        onClick={handleWhatsAppClick}
        className={cn(
          "relative w-14 h-14 md:w-16 md:h-16 bg-[#25D366] text-white rounded-full",
          "shadow-lg hover:shadow-xl transition-all duration-300",
          "hover:scale-110 active:scale-95",
          "animate-pulse-slow"
        )}
        aria-label="Falar no WhatsApp"
      >
        <MessageCircle className="w-6 h-6 md:w-8 md:h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        
        {/* Online indicator */}
        {isWorkingHours() && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white">
            <div className="absolute inset-0 w-full h-full bg-green-400 rounded-full animate-ping"></div>
          </div>
        )}

        {/* Pulse ring */}
        <div className="absolute inset-0 rounded-full bg-[#25D366] opacity-20 animate-ping"></div>
      </button>
    </div>
  );
};

export default WhatsAppFloat;